import { useContext, useEffect, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import useGet from "../hooks/useGet";
import BlogContentBlock from "../components/blog/BlogContentBlock";
import backIcon from "../assets/icons/arrow-left-white.svg";
import previousIcon from "../assets/icons/chevron-left-gold.svg";
import nextIcon from "../assets/icons/chevron-right-gold.svg";
import SEO from "../components/shared/SEO";
import NewsletterForm from "../components/shared/NewsletterForm";
import poweredByContext from "../store/PoweredByContext";
import LazyLoadingContext from "../store/LazyLoadingContext";
import { getUrl } from "../utilities/url";
import QuoteQueryJets from "../components/shared/QuoteQueryJets";
import getClassName from "../utilities/getClassName";

const BlogArticle = () => {
    const articleSlug = useParams().article;
    const { error, returnData: article } = useGet(`/api/content/article/${articleSlug}`);

    const poweredByCtx = useContext(poweredByContext);
    const lazyLoadingCtx = useContext(LazyLoadingContext);
    const containerRef = useRef(null);

    // ensure any previous prerender flag is cleared on mount/unmount (important for persistent browser contexts)
    useEffect(() => {
        try {
            window.__PRERENDER_READY = false;
        } catch (e) { /* ignore */ }

        return () => {
            try {
                window.__PRERENDER_READY = false;
            } catch (e) { /* ignore */ }
        };
    }, []);

    // keep data-prerender-ready attribute and window flag in sync with page readiness
    useEffect(() => {
        const isReady = lazyLoadingCtx.pageReady && article !== "" && article !== false;

        try {
            if (isReady) {
                window.__PRERENDER_READY = true;
                if (containerRef.current) {
                    containerRef.current.setAttribute("data-prerender-ready", "true");
                }
            } else {
                window.__PRERENDER_READY = false;
                if (containerRef.current) {
                    containerRef.current.removeAttribute("data-prerender-ready");
                }
            }
        } catch (e) {
            // ignore any cross-origin or SSR issues
        }
    }, [lazyLoadingCtx.pageReady, article]);

    useEffect(() => {
        return () => {
            lazyLoadingCtx.reset("blogArticle");
        };
        // Intentionally mount-only: re-firing would call reset() and clobber
        // page-ready state that other effects have already populated.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (article !== false && article !== "") {
            lazyLoadingCtx.updatePageCheck("blogArticle", {
                article: true,
            });
        }
    }, [article, lazyLoadingCtx]);

    // Function to dynamically create heading element
    const renderHeading = (title, tag, sub) => {
        const Tag = tag ? tag.toLowerCase() : sub ? "h2" : "h1";
        if (sub) {
            return <Tag className="brochure__subtitle">{title}</Tag>;
        }
        return <Tag className="brochure__title blog-article__title">{title}</Tag>;
    };

    const buildDate = (date) => {
        const months = [
            "January",
            "February",
            "March",
            "April",
            "May",
            "June",
            "July",
            "August",
            "September",
            "October",
            "November",
            "December",
        ];

        const d = new Date(date);

        return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
    };

    const navigate = useNavigate();

    useEffect(() => {
        if (error) {
            navigate("/blog");
        }
    }, [error, navigate]);

    return (
        <>
            {article && (
                <SEO
                    title={article.title}
                    description={article.metaDescription}
                    type="article"
                    image={article.imageHolder?.url}
                    date={article.articleDate}
                    canonicalUrl={getUrl(poweredByCtx, window.location.pathname)}
                />
            )}

            {(!(lazyLoadingCtx.pageReady && article !== "" && article !== false) || article?.stickyQuoteBool) && (
                <div className="wrap">
                    <QuoteQueryJets
                        onPage
                        skeleton={!(lazyLoadingCtx.pageReady && article !== "" && article !== false)}
                    />
                </div>
            )}

            <div
                ref={containerRef}
                className={getClassName([
                    "blog-article",
                    lazyLoadingCtx.pageReady && article ? "" : "blog-article--skeleton",
                ])}
            >
                {lazyLoadingCtx.pageReady && article ? (
                    <>
                        <header className="blog-article__header">
                            {renderHeading(article.title, article.headingTitle, false)}
                            <p className="blog-article__date">{buildDate(article.articleDate)}</p>
                            <Link to="/blog" className="blog-article__back">
                                <img src={backIcon} alt="" />
                                All posts
                            </Link>
                        </header>

                        <figure className="blog-article__image">
                            <img src={article.imageHolder.url} alt={article.imageHolder.alt || ""} />
                        </figure>

                        <div className="blog-article__content">
                            {article.contentBlocks.map((data, index) => {
                                return <BlogContentBlock key={index} data={data} />;
                            })}
                            <div className="blog-article__cta">
                                <p>
                                    Do not hesitate to visit our online quote to obtain a price estimate for your next
                                    journey.
                                </p>
                                <a href="/" className="button">
                                    Request a quote
                                </a>
                            </div>
                        </div>

                        {(article.prev || article.next) && (
                            <footer className="blog-article__footer">
                                <nav className="blog-article__navigation">
                                    {article.prev && (
                                        <Link
                                            to={`/blog/${article.prev.urlSlug}`}
                                            className="blog-article__link blog-article__link--previous"
                                        >
                                            <img src={previousIcon} alt="" />
                                            Previous
                                            <small>{article.prev.title}</small>
                                        </Link>
                                    )}

                                    {article.next && (
                                        <Link
                                            to={`/blog/${article.next.urlSlug}`}
                                            className="blog-article__link blog-article__link--next"
                                        >
                                            <img src={nextIcon} alt="" />
                                            Next
                                            <small>{article.next.title}</small>
                                        </Link>
                                    )}
                                </nav>
                            </footer>
                        )}
                    </>
                ) : (
                    <>
                        <header className="blog-article__header">
                            <h1 className="blog-article__title">Article title</h1>
                            <p className="blog-article__date">Article date</p>
                            <Link to="/blog" className="blog-article__back">
                                <img src={backIcon} alt="" />
                                All posts
                            </Link>
                        </header>

                        <figure className="blog-article__image"></figure>
                    </>
                )}
            </div>

            <NewsletterForm />
        </>
    );
};

export default BlogArticle;
